/* eslint-disable no-shadow */
/* eslint-disable no-unused-vars */
/* eslint-disable consistent-return */
import { useState, useCallback } from "react";

import { useMsal } from "@azure/msal-react";
import { useNativeAuth } from "context/NativeAuthContext";

/**
 * Custom hook to call a web API using bearer token from Native Auth or MSAL.
 * Does not use useMsalAuthentication (Popup) so native-signed-in users are not shown a Microsoft login popup.
 * @param {object} msalRequest - { scopes } for MSAL token when no native token
 * @returns
 */
function normalizeScopes(scopes) {
  if (!scopes || !Array.isArray(scopes)) return scopes;
  const first = scopes[0];
  if (Array.isArray(first)) return first;
  return scopes;
}

const useFetchWithMsal = (msalRequest) => {
  const { instance } = useMsal();
  const { getValidAccessToken, refreshAccessToken, endSession, sessionRecoverable } =
    useNativeAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const scopes = normalizeScopes(msalRequest?.scopes) ?? msalRequest?.scopes;

  /**
   * Execute a fetch request with the given options.
   * Uses native auth token first; if none, uses MSAL silent token (no popup).
   * @param {string} method: GET, POST, PUT, DELETE
   * @param {String} endpoint: The endpoint to call
   * @param {Object} data: The data to send to the endpoint, if any
   * @returns JSON response
   */
  const execute = useCallback(
    async (method, endpoint, data = null) => {
      // Native token first — refreshes silently if it's expired/near-expiry, so an active user's
      // request is never dropped just because the 30-min access token lapsed.
      let accessToken = await getValidAccessToken();
      if (!accessToken) {
        const account = instance.getActiveAccount();
        if (account && scopes?.length) {
          try {
            const silent = await instance.acquireTokenSilent({ scopes, account });
            accessToken = silent?.accessToken;
          } catch (e) {
            setError(e);
            return;
          }
        }
      }

      if (accessToken) {
        try {
          const headers = new Headers();
          headers.append("Authorization", `Bearer ${accessToken}`);
          if (data) headers.append("Content-Type", "application/json");

          setIsLoading(true);
          const body = data ? JSON.stringify(data) : null;
          let res = await fetch(endpoint, { method, headers, body });
          // Belt-and-suspenders: if the token lapsed between check and call, refresh once and retry
          // the SAME request — so the user's action (e.g. a save) still lands instead of silently
          // failing and bouncing them to sign-in with unsaved work.
          if (res.status === 401) {
            const renewed = await refreshAccessToken();
            if (renewed) {
              headers.set("Authorization", `Bearer ${renewed}`);
              res = await fetch(endpoint, { method, headers, body });
            } else if (sessionRecoverable()) {
              // Refresh failed but a refresh token is still stored → TRANSIENT (network/proxy 5xx),
              // not a dead session. Keep the ACTIVE user signed in and surface a retryable error
              // instead of bouncing them to sign-in mid-task.
              setIsLoading(false);
              return {
                success: false,
                message: "Temporary sign-in issue — please try again.",
                status: 401,
              };
            } else {
              // No refresh token left → the session is genuinely over. Log out cleanly instead of
              // returning a 401 the calling component may not handle (→ blank).
              endSession();
              setIsLoading(false);
              return {
                success: false,
                message: "Your session has expired. Please sign in again.",
                status: 401,
              };
            }
          }
          const text = await res.text();
          const trimmed = text ? text.trim() : "";
          let response;
          if (!trimmed) {
            response = {};
          } else if (trimmed.startsWith("<")) {
            console.error(
              "[useFetchWithMsal] Got HTML instead of JSON — check API base URL (/api). Request:",
              res.status,
              endpoint
            );
            response = {
              success: false,
              message:
                "The server returned a web page instead of API data. Set REACT_APP_API_URL at build time or siteConfig apiBaseUrl for this site (full base with /api, e.g. https://qantas.local.6thsenserisk.com/api), and ensure your reverse proxy routes /api to the Risk API.",
              status: res.status,
            };
          } else {
            try {
              response = JSON.parse(trimmed);
            } catch (parseErr) {
              console.error("[useFetchWithMsal] JSON parse failed:", endpoint, parseErr);
              response = {
                success: false,
                message: trimmed.slice(0, 240) || parseErr.message,
                status: res.status,
              };
            }
          }
          setData(response);
          setIsLoading(false);
          return response;
        } catch (e) {
          setError(e);
          setIsLoading(false);
          throw e;
        }
      }
      // No usable access token. If a refresh token is still stored, this is a TRANSIENT refresh
      // failure — keep the ACTIVE user signed in and let them retry, rather than bouncing to sign-in.
      if (sessionRecoverable()) {
        setIsLoading(false);
        return {
          success: false,
          message: "Temporary sign-in issue — please try again.",
          status: 401,
        };
      }
      // No refresh path left — the native session has genuinely expired. End it so the click lands on
      // /signin instead of a blank/unresponsive dashboard. endSession clears tokens and hard-redirects;
      // we still return a safe shape so any in-flight caller doesn't throw on `res.data`/`res.success`
      // before the navigation lands (that crash is what showed a blank screen).
      endSession();
      setIsLoading(false);
      return {
        success: false,
        message: "Your session has expired. Please sign in again.",
        status: 401,
      };
    },
    [getValidAccessToken, refreshAccessToken, endSession, sessionRecoverable, instance, scopes]
  );

  return {
    isLoading,
    error,
    data,
    execute,
  };
};

export default useFetchWithMsal;
